// ConnectionStatus.jsx
import React, { useState, useEffect } from "react";
import {
  Chip,
  Fade,
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import SignalWifi4BarIcon from "@mui/icons-material/SignalWifi4Bar";
import { useTheme } from "@mui/material/styles";

export const ConnectionStatus = ({ isConnected }) => {
  const [connecting, setConnecting] = useState(!isConnected);
  const theme = useTheme();

  // Show connecting animation when status changes
  useEffect(() => {
    let timer;
    if (!isConnected) {
      setConnecting(true);
    } else {
      timer = setTimeout(() => {
        setConnecting(false);
      }, 2000);
    }
    return () => clearTimeout(timer);
  }, [isConnected]);

  if (!isConnected && !connecting) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 70,
          right: 16,
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 2,
          borderRadius: 2,
          backgroundColor: theme.palette.error.main + "20",
          border: `2px solid ${theme.palette.error.main}`,
          backdropFilter: "blur(10px)",
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 4px 20px rgba(0, 0, 0, 0.4)`
              : `0 4px 20px rgba(0, 0, 0, 0.1)`,
        }}
      >
        <WifiOffIcon sx={{ color: theme.palette.error.main }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: theme.palette.error.main }}
        >
          Disconnected
        </Typography>
      </Box>
    );
  }

  const statusColor = isConnected
    ? theme.palette.success.main
    : connecting
    ? theme.palette.info.main
    : theme.palette.error.main;

  const statusBgColor = isConnected
    ? theme.palette.success.light + "20"
    : connecting
    ? theme.palette.info.light + "20"
    : theme.palette.error.light + "20";

  return (
    <Box
      sx={{
        position: "fixed",
        top: 70,
        right: 16,
        zIndex: 9999,
      }}
    >
      <Tooltip
        title={
          isConnected
            ? "Connected to kitchen device"
            : connecting
            ? "Connecting to kitchen device..."
            : "Kitchen device disconnected"
        }
        arrow
        placement="left"
      >
        <Chip
          icon={
            isConnected ? (
              <SignalWifi4BarIcon sx={{ color: theme.palette.success.main }} />
            ) : (
              <WifiOffIcon sx={{ color: statusColor }} />
            )
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {isConnected
                  ? "Connected"
                  : connecting
                  ? "Connecting..."
                  : "Disconnected"}
              </Typography>
              {connecting && (
                <LinearProgress
                  size={16}
                  sx={{
                    width: 40,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: theme.palette.info.light + "40",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: theme.palette.info.main,
                    },
                  }}
                />
              )}
            </Box>
          }
          sx={{
            backgroundColor: statusBgColor,
            color: statusColor,
            border: `2px solid ${statusColor}`,
            fontWeight: 600,
            px: 2,
            py: 1,
            borderRadius: 3,
            backdropFilter: "blur(10px)",
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 4px 20px rgba(0, 0, 0, 0.4)`
                : `0 4px 20px rgba(0, 0, 0, 0.1)`,
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow:
                theme.palette.mode === "dark"
                  ? `0 6px 24px rgba(0, 0, 0, 0.5)`
                  : `0 6px 24px rgba(0, 0, 0, 0.15)`,
            },
          }}
        />
      </Tooltip>
    </Box>
  );
};
